/**
 * File Name : ToastNotification.tsx
 * Component Name : ToastNotification
 * @props : IToastNotification
 * @description : This will show the toast notification.
 */

import React from 'react';
import { toast } from 'react-toastify';
import { IToastNotification } from './Interfaces/ToastNotification.interface';
import './toastNotification.scss';
import { useIsInViewport } from '../../hooks/useIsInViewport';
import { IToastType } from './ToastNotification.constants';

export const ToastNotification: React.FunctionComponent<IToastNotification> = (props: IToastNotification) => {
  const {
    notificationIcon,
    notificationMessage,
    exludeHeaderHeight = true,
    notificationType = IToastType.default,
    notificationIconAltText = '',
    isBreadcrumbAvailable = true
  } = props;
  const toastTypeClass = notificationType;
  const iconImage = `${notificationIcon}`;

  // Get main content Area container
  const mainElem: HTMLDivElement = document.querySelector('#main-content');
  const { top: mainTopOffset } = mainElem.getBoundingClientRect();
  // Calculate if the container is scrolled passed the viewport
  const mainTopPadding = mainTopOffset < 0 ? Math.abs(mainTopOffset) : 0;
  let heightWithBreadcrumb;
  // Get breadcrumbs container
  /**
   * Need to be decoupled and remove dependency from Breadcrumbs container
   * Tech Debt item: https://dev-mc.visualstudio.com/Minecraft/_workitems/edit/1273342
   */
  const breadcrumbElem: HTMLDivElement | HTMLHeadElement = isBreadcrumbAvailable ? document.querySelector('.layout__breadcrumbs') : document.querySelector('header');
  const isBreadcrumbsInViewport = useIsInViewport({ current: breadcrumbElem });
  if (isBreadcrumbAvailable) {
    const { clientHeight: breadcrumbHeight } = breadcrumbElem;
    // Get header container
    const headerElem: HTMLHeadElement = document.querySelector('header');
    // If page at the top and exclude header required then return 0, otherwise return header height
    const { clientHeight: headerHeight } = exludeHeaderHeight && mainTopOffset > 0 ? { clientHeight: 0 } : headerElem;
    heightWithBreadcrumb = isBreadcrumbsInViewport ? headerHeight + breadcrumbHeight : headerHeight;
  } else {
    const headerElem: HTMLHeadElement = document.querySelector('header');
    // If page at the top and exclude header required then return 0, otherwise return header height
    const { clientHeight: headerHeight } = exludeHeaderHeight && mainTopOffset > 0 ? { clientHeight: 0 } : headerElem;
    heightWithBreadcrumb = headerHeight;
  }

  // Calculate the toast position:
  // Main content top offset + header + breadcrumb
  const toastTop = mainTopPadding + heightWithBreadcrumb;

  const updateToastContainer = () => {
    const toastContainer: HTMLDivElement = document.querySelector('.Toastify__toast-container');

    if (!toastContainer) {
      setTimeout(updateToastContainer, 100);
    } else {
      toastContainer.style.setProperty('--toast-top', `${toastTop}`);
    }
  };

  // Check for toast container to exist first then
  setTimeout(updateToastContainer, 100);

  return (
    <div className={`notification ${toastTypeClass}`}>
      <div className="notification__icon">
        <img className="notification__iconimage" alt={notificationIconAltText} src={iconImage} />
      </div>
      <div className="notification__message">{notificationMessage}</div>
    </div>
  );
};
export const notify = (
  icon: string,
  message: string,
  exHeaderHeight?: boolean,
  notificationTypeValue?: IToastType,
  iconAltText?: string,
  isBreadcrumbAvailable?: boolean,
  options: Object = {}
) => {
  message &&
    toast(
      <ToastNotification
        notificationIcon={icon}
        notificationMessage={message}
        exludeHeaderHeight={exHeaderHeight}
        notificationType={notificationTypeValue}
        notificationIconAltText={iconAltText}
        isBreadcrumbAvailable={isBreadcrumbAvailable}
      />,
      {
        closeButton: false,
        draggable: false,
        theme: 'dark',
        position: 'top-center',
        hideProgressBar: true,
        autoClose: 5000,
        ...options
      }
    );
};
